import { useEffect, useState } from "react";
import { Smartphone, Share, MoreVertical, PlusSquare, CheckCircle2, Download } from "lucide-react";
import { isIOS, isStandalone } from "./device";
import { Btn, Card } from "./ui";

const Phone = ({ children, bottom }) => (
  <div className="mx-auto w-[108px] h-[150px] rounded-[18px] border-2 border-[#3A3A42] bg-[#0E0E10] relative overflow-hidden shrink-0">
    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-8 h-1.5 rounded-full bg-[#2E2E34]" />
    <div className={`absolute inset-x-2 ${bottom ? "bottom-2" : "top-6"} space-y-1.5`}>{children}</div>
  </div>
);
const Line = ({ w = "w-full", hi }) => <div className={`h-2 rounded ${w} ${hi ? "bg-[#E5B13A]" : "bg-[#26262B]"}`} />;
const Row = ({ icon: Icon, hi, label }) => (
  <div className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${hi ? "bg-[#E5B13A] text-[#15120A]" : "bg-[#1C1C20] text-[#6F6A62]"}`}>
    <Icon size={10} /><span className="text-[7px] font-semibold leading-none truncate">{label}</span>
  </div>
);

const IOS_STEPS = [
  { t: "Apri questa pagina in Safari", d: "Se l'hai aperta da WhatsApp o Instagram (vedi una X o «Fine» in alto), tocca il simbolo Safari in basso a destra, oppure i tre puntini e «Apri in Safari».", img: <Phone bottom><Line w="w-3/4" /><Line /><div className="flex justify-around pt-2 text-[#6F6A62]"><span className="text-[9px]">‹</span><span className="text-[9px]">›</span><span className="rounded-md bg-[#E5B13A] text-[#15120A] px-1.5 py-0.5"><Share size={10} /></span><span className="text-[9px]">▢</span></div></Phone> },
  { t: "Tocca il tasto Condividi", d: "È il quadratino con la freccia in su, in basso al centro della barra di Safari. Non serve tenere premuto nulla.", img: <Phone bottom><Row icon={Share} label="Copia" /><Row icon={Share} label="Aggiungi ai preferiti" /><Row icon={PlusSquare} hi label="Aggiungi alla schermata Home" /></Phone> },
  { t: "Scegli «Aggiungi alla schermata Home»", d: "Scorri il menu verso il basso se non la vedi subito. Poi tocca «Aggiungi» in alto a destra.", img: <Phone><div className="flex justify-between items-center"><span className="text-[7px] text-[#6F6A62]">Annulla</span><span className="text-[7px] font-bold text-[#E5B13A]">Aggiungi</span></div><div className="flex items-center gap-1.5 pt-2"><img src="/club/icon-192.png" alt="" className="w-6 h-6 rounded-md" /><Line w="w-14" /></div></Phone> },
];
const ANDROID_STEPS = [
  { t: "Apri questa pagina in Chrome", d: "Se l'hai aperta da WhatsApp, tocca i tre puntini in alto e scegli «Apri in Chrome».", img: <Phone><div className="flex justify-between items-center"><Line w="w-16" /><span className="rounded bg-[#E5B13A] text-[#15120A] p-0.5"><MoreVertical size={10} /></span></div><Line /><Line w="w-3/4" /></Phone> },
  { t: "Tocca i tre puntini ⋮ in alto a destra", d: "Si apre il menu di Chrome.", img: <Phone><Row icon={Share} label="Condividi" /><Row icon={Download} hi label="Installa app" /><Row icon={PlusSquare} label="Aggiungi a schermata Home" /></Phone> },
  { t: "Scegli «Installa app» (o «Aggiungi a schermata Home»)", d: "Conferma con «Installa». Trovi l'icona Diamond Club tra le tue app.", img: <Phone><div className="flex items-center gap-1.5 pt-6"><img src="/club/icon-192.png" alt="" className="w-7 h-7 rounded-lg" /><div className="space-y-1"><Line w="w-12" hi /><Line w="w-8" /></div></div></Phone> },
];

export default function InstallGuide({ compact = false }) {
  const [os, setOs] = useState(isIOS() ? "ios" : "android");
  const [open, setOpen] = useState(!compact && !isStandalone());
  const [prompt, setPrompt] = useState(null);
  const [done, setDone] = useState(isStandalone());

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    const inst = () => setDone(true);
    window.addEventListener("appinstalled", inst);
    return () => { window.removeEventListener("beforeinstallprompt", h); window.removeEventListener("appinstalled", inst); };
  }, []);

  const steps = os === "ios" ? IOS_STEPS : ANDROID_STEPS;

  return (
    <Card className="border-[#E5B13A]/30" data-testid="install-guide">
      <div className="flex gap-3 items-start">
        <Smartphone size={20} className="text-[#E5B13A] mt-0.5 shrink-0" />
        <div className="text-sm flex-1">
          <p className="font-semibold">{done ? "Hai salvato la pagina sul telefono?" : "Salva la tua pagina sul telefono"}</p>
          <p className="text-[#9A948A] mt-1">{done ? "Se la apri già dall'icona Diamond Club, perfetto. Se invece la stai leggendo da un link, qui sotto trovi come salvarla." : "Così la ritrovi con un tocco, come un'app, e possiamo mandarti i promemoria. Ci vogliono 20 secondi."}</p>
        </div>
      </div>
      {prompt && (
        <Btn className="mt-4 w-full" onClick={async () => { prompt.prompt(); const r = await prompt.userChoice; if (r.outcome === "accepted") setDone(true); setPrompt(null); }} data-testid="install-now-btn"><Download size={16} /> Installa adesso</Btn>
      )}
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-3 text-xs text-[#F5D27A] underline-offset-2 hover:underline" data-testid="install-guide-open">Mostrami come si fa, passo per passo</button>
      ) : (
        <>
          <div className="mt-4 flex gap-1 rounded-full bg-[#0E0E10] p-1 text-xs font-semibold" data-testid="install-os-switch">
            {[["ios", "iPhone"], ["android", "Android"]].map(([k, l]) => (
              <button key={k} onClick={() => setOs(k)} data-testid={`install-os-${k}`} className={`flex-1 rounded-full py-2 transition-colors ${os === k ? "bg-[#E5B13A] text-[#15120A]" : "text-[#9A948A]"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-4 space-y-4">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-3 items-center" data-testid={`install-step-${i + 1}`}>
                {s.img}
                <div className="text-sm"><p className="font-semibold"><span className="font-club-display text-[#E5B13A] mr-1.5">{i + 1}.</span>{s.t}</p><p className="text-xs text-[#9A948A] mt-1 leading-relaxed">{s.d}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 items-start text-xs text-[#9A948A] rounded-2xl bg-[#0E0E10] p-3">
            <CheckCircle2 size={14} className="text-[#25D366] mt-0.5 shrink-0" />
            <p>Attenzione: tenere premuto il dito sulla pagina o sull'icona <b className="text-[#F4EFE6]">non serve</b>. Il trucco è tutto nel tasto {os === "ios" ? "Condividi" : "⋮"}.</p>
          </div>
        </>
      )}
    </Card>
  );
}
